import React from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import logo from "../../../images/Agtellio_logo_cropped.png";
import LoginAvatar from "../LoginAvatar";
import { useWindowSize } from "react-use";
import { useSelector } from "react-redux";
import authService from "../../api-authorization/AuthorizeService";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import styles from "./Navbar.module.css";

export const joinClasses = (...classes) => {
  return classes.map((className) => styles[className]).join(" ");
};

// Helper to get ActiveClientId from cookie
const getActiveClientIdFromCookie = () => {
  const cookies = document.cookie.split(';');
  const activeClientCookie = cookies.find(c => c.trim().startsWith('ActiveClientId='));
  if (activeClientCookie) {
    const value = activeClientCookie.split('=')[1];
    return value ? Number(value) : undefined;
  }
  return undefined;
};

export const Navbar = () => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [clients, setClients] = React.useState([]);
  
  // SRead ONLY from cookie - no localStorage
  const [activeClientId, setActiveClientIdState] = React.useState(() => {
    return getActiveClientIdFromCookie();
  });

  const isAdmin = useSelector((state) => state.app.isAdmin);
  const userClientId = useSelector((state) => state.app.clientId);
  const navigate = useNavigate();

  const { pathname } = useLocation();
  const isHome =
    pathname === "/home" ||
    pathname === "" ||
    pathname === "#" ||
    pathname === "/";

  const { width } = useWindowSize();

  React.useEffect(() => {
    if (width > 1165) setIsOpen(false);
  }, [width]);

  // Fetch clients and set initial value
  React.useEffect(() => {
    if (!isAdmin) return;
    
    let ignore = false;
    
    const fetchClients = async () => {
      try {
        const token = await authService.getAccessToken();
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/api/admin/clients/all`,
          {
            headers: !token ? {} : { Authorization: `Bearer ${token}` }
          }
        );
        const clientData = await response.json();
        
        if (!ignore) {
          setClients(clientData);

          // Get current value from cookie
          const cookieClientId = getActiveClientIdFromCookie();
          
          if (clientData.length > 0) {
            // Determine which client to select
            let clientToSelect;
            
            if (cookieClientId && clientData.some(c => c.id === cookieClientId)) {
              // Use cookie value if valid
              clientToSelect = cookieClientId;
            } else if (userClientId && clientData.some(c => c.id === userClientId)) {
              // Use user's default client if cookie invalid
              clientToSelect = userClientId;
            } else {
              // Fall back to first client
              clientToSelect = clientData[0].id;
            }
            
            // Update state and cookie if needed
            if (clientToSelect !== cookieClientId) {
              setActiveClientIdState(clientToSelect);
              await setActiveClientOnServer(clientToSelect);
            }
          }
        }
      } catch (error) {
        console.error("Failed to fetch clients:", error);
      }
    };
    
    fetchClients();
    
    return () => {
      ignore = true;
    };
    // eslint-disable-next-line
  }, [isAdmin, userClientId]);

  // Handle client change
  const handleClientChange = async (event, newValue) => {
    if (!newValue) {
      if (clients.length > 0) {
        setActiveClientIdState(clients[0].id);
        await setActiveClientOnServer(clients[0].id);
        window.location.href = '/dashboard'; // ✅ Force full reload
      }
      return;
    }
    
    setActiveClientIdState(newValue.id);
    await setActiveClientOnServer(newValue.id);
    window.location.href = '/dashboard'; // ✅ Force full reload
  };

  // Call backend to set cookie
  const setActiveClientOnServer = async (clientId) => {
    try {
      const token = await authService.getAccessToken();
      await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/users/set-active-client`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          body: JSON.stringify(clientId)
        }
      );
    } catch (error) {
      console.error("Failed to set active client:", error);
    }
  };

  // Find selected client
  const selectedClient =
    clients.find((c) => c.id === activeClientId) || (clients.length > 0 ? clients[0] : null);

  return (
    <header className={styles.navBar}>
      {/* lg+ */}
      <div
        className={
          " relative  shadow-[var(--navbar-box-shadow)]  bg-[var(--navbar-gradient)]"
        }
      >
        <div className="absolute inset-0" />
        <div className="relative px-4 mx-auto sm:px-6 navbarlg:px-8">
          <nav className="flex items-center justify-between h-16 navbarlg:h-20 flex-row-reverse navbarlg:flex-row">
            <div className="block navbarlg:hidden flex-shrink-0">
              <LoginAvatar loggedIn />
            </div>

            <div className="hidden navbarlg:block flex-shrink-0">
              <a href="#" title="" className="flex">
                <img
                  className="navbar-agtellio-logo"
                  src={logo}
                  alt=""
                  width={115}
                  height={50}
                />
              </a>
            </div>

            <button
              onClick={() => {
                setIsOpen(!isOpen);
              }}
              id="hamburger"
              type="button"
              className={`flex-shrink-0 inline-flex p-2 text-white transition-all duration-200 rounded-md navbarlg:hidden focus:bg-gray-800 hover:bg-gray-800 + " " + ${
                !isHome ? "" : "opacity-0 pointer-events-none"
              } `}
            >
              <svg
                className="w-6 h-6"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16m-7 6h7"
                />
              </svg>
            </button>
            {!isHome && (
              <div className="hidden navbarlg:flex navbarlg:items-center space-x-4  navbarsmallelts:space-x-10   ">
                <Link
                  to="/dashboard"
                  title=""
                  className="text-[12px] navbarsmallelts:text-[15px]  font-medium text-white"
                >
                  Dashboard
                </Link>
                <Link
                  to="/precision-spray"
                  title=""
                  className="text-[12px] navbarsmallelts:text-[15px] font-medium text-white"
                >
                  Sprayers
                </Link>
                <Link
                  to="/reports"
                  title=""
                  className="text-[12px] navbarsmallelts:text-[15px]  font-medium text-white"
                >
                  Reports
                </Link>
                <Link
                  to="/savings"
                  title=""
                  className="text-[12px] navbarsmallelts:text-[15px]  font-medium text-white"
                >
                  Savings
                </Link>
                <Link
                  to="/tech-support"
                  title=""
                  className="text-[12px] navbarsmallelts:text-[15px]  font-medium text-white"
                >
                  Support
                </Link>
                <a
                  className="text-[12px] navbarsmallelts:text-[15px]  font-medium text-white"
                  href="https://www.agtechlogic.shop"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Shop
                </a>
                {/* Active Client Dropdown for Admins */}
                {isAdmin && clients.length > 0 && (
                  <Autocomplete
                    options={clients}
                    getOptionLabel={(option) => option.name}
                    value={selectedClient}
                    onChange={handleClientChange}
                    sx={{ width: 260, background: "white", borderRadius: 1 }}
                    size="small"
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                )}
              </div>
            )}
            <div
              className="items-center justify-center hidden px-6 py-3 text-base font-semibold text-black transition-all duration-200 navbarlg:inline-flex "
            >
              <LoginAvatar loggedIn />
            </div>
          </nav>
        </div>
      </div>
      {/* xs to lg */}
      <nav
        className={`flex flex-col justify-between px-4 py-10 sm:px-6 transition-all origin-top navbarlg:hidden ${
          isOpen
            ? "scale-y-100 opacity-100 visible "
            : "scale-y-0 opacity-0 invisible h-0 !p-0"
        }`}
      >
        <div className="flex flex-col flex-grow h-full">
          <nav className="flex flex-col flex-1 mt-4 space-y-2">
            <Link
              to="/dashboard"
              title=""
              className="flex w-full py-2 font-medium text-white transition-all duration-200 focus:text-opacity-70"
            >
              {" "}
              Dashboard{" "}
            </Link>
            <Link
              to="/precision-spray"
              title=""
              className="flex w-full py-2 font-medium text-white transition-all duration-200 focus:text-opacity-70"
            >
              {" "}
              Sprayers{" "}
            </Link>
            <Link
              to="/reports"
              title=""
              className="flex w-full py-2 font-medium text-white transition-all duration-200 focus:text-opacity-70"
            >
              {" "}
              Reports{" "}
            </Link>
            <Link
              to="/savings"
              title=""
              className="flex w-full py-2 font-medium text-white transition-all duration-200 focus:text-opacity-70"
            >
              {" "}
              Savings{" "}
            </Link>
            <Link
              to="/tech-support"
              title=""
              className="flex w-full py-2 font-medium text-white transition-all duration-200 focus:text-opacity-70"
            >
              {" "}
              Support{" "}
            </Link>
            <a
              target="_blank"
              rel="noopener noreferrer"
              className="flex w-full py-2 font-medium text-white transition-all duration-200 focus:text-opacity-70"
              href="https://www.agtechlogic.shop"
            >
              Shop
            </a>
            {/* Active Client Dropdown for Admins (mobile) */}
            {isAdmin && clients.length > 0 && (
              <Autocomplete
                options={clients}
                getOptionLabel={(option) => option.name}
                value={selectedClient}
                onChange={handleClientChange}
                sx={{ width: 260, mt: 2, background: "white", borderRadius: 1 }}
                size="small"
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            )}
          </nav>
        </div>
      </nav>
    </header>
  );
};

export default Navbar;
